import { useState, useCallback, useEffect } from 'react';
import * as errorCodes from '../common/errorCodes';
import { getLabelFromJspVariables } from '../common/JspUtil';
import { CheckmarkIcon, Dropdown, PhoneIcon, TextInput, DialogTitleWithClose } from '../components';
import PhoneVerifier from './PhoneVerifier';
import Alert from '@mui/material/Alert';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import MuiDialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Skeleton from '@mui/material/Skeleton';
import Grid from '@mui/material/Unstable_Grid2';

export const UserContactSkeleton = () => (
    <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
            <Skeleton variant="rounded" height={56} sx={{ flex: 1, minWidth: { xs: '100%', md: 0 } }} />
            <Skeleton variant="rounded" height={56} sx={{ flex: 1, minWidth: { xs: '100%', md: 0 } }} />
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
            <Skeleton variant="rounded" height={56} sx={{ flex: 1, minWidth: { xs: '100%', md: 0 } }} />
            <Skeleton variant="rounded" height={56} sx={{ flex: 1, minWidth: { xs: '100%', md: 0 } }} />
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
            <Skeleton variant="rounded" height={56} sx={{ flex: 1, minWidth: { xs: '100%', md: 0 } }} />
            <Skeleton variant="rounded" height={56} sx={{ flex: 1, minWidth: { xs: '100%', md: 0 } }} />
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
            <Skeleton variant="rounded" height={56} sx={{ flex: 1, minWidth: { xs: '50%', md: 0 } }} />
            <Skeleton variant="rounded" height={56} sx={{ flex: 1, minWidth: { xs: '50%', md: 0 } }} />
            <Skeleton variant="rounded" height={56} sx={{ flex: 1, minWidth: { xs: '50%', md: 0 } }} />
            <Skeleton variant="rounded" height={56} sx={{ flex: 1, minWidth: { xs: '50%', md: 0 } }} />
        </Box>
    </Box>
);

const PhoneInput = ({ label, value, areaCode, onChange, onAreaCodeChange, required, readOnly, verified, verify, areacodes, phoneError, phoneHelperText, codeError, codeHelperText }) => {
    const labelSlug = String(label).trim().toLowerCase().replace(/\s+/g, '_');
    const phoneCodeEntry = areacodes && areacodes.find(c => c.code === areaCode);
    const matchesRegex = !phoneCodeEntry?.phoneNumRegex
        || (!!value && new RegExp(phoneCodeEntry.phoneNumRegex).test(value));
    const canVerify = !!value && !!areaCode && matchesRegex;

    return (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Dropdown selectedValue={areaCode}
                id={`phone_area_code_${labelSlug}`}
                name={`phone_area_code_${labelSlug}`}
                dataAutomationId={`phone_area_code_${labelSlug}`}
                label={getLabelFromJspVariables('label_code')}
                ariaLabelledBy={`phone_area_code_${labelSlug}`}
                labelProperty="displayCode"
                valueProperty="code"
                required={required}
                options={areacodes}
                disabled={readOnly}
                error={codeError}
                helperText={codeHelperText}
                sx={{ flex: '0 0 140px' }}
                updateValue={onAreaCodeChange} />
            <TextInput
                label={label}
                id={`phone_input_${labelSlug}`}
                maxLength={30}
                numericOnly
                value={value}
                required={required}
                disabled={readOnly}
                error={phoneError}
                helperText={phoneHelperText}
                onChange={onChange}
                sx={{ flex: 1 }}
            />
            {!readOnly && (
                verified ?
                    <CheckmarkIcon size={20} />
                    :
                    <Tooltip title={canVerify ? 'Click to verify phone number' : 'Enter a valid phone code and number to verify'} placement="top">
                        <IconButton onClick={verify} disabled={!canVerify} aria-label="Verify phone number"
                            data-automation-id={`user_contact_verify_phone_${labelSlug}_btn`}>
                            <PhoneIcon size={18} />
                        </IconButton>
                    </Tooltip>
            )}
        </Box>
    );
};

const requiredFields = [
    { v: 'firstName', l: 'First name' },
    { v: 'lastName', l: 'Last name' },
    { v: 'phoneCode', l: 'Code' },
    { v: 'phone', l: 'Phone number' },
    { v: 'address1', l: 'Address line 1' },
    { v: 'city', l: 'City' },
    { v: 'countryCode', l: 'Country' },
];

export function validateUserContact(userProfile, ssoEnvironment, areacodes, countryByCode) {
    const fieldErrors = {};
    for (let field of requiredFields) {
        if (!userProfile[field.v]) {
            fieldErrors[field.v] = field.l + ' is required';
        }
    }
    const country = countryByCode && userProfile.countryCode ? countryByCode[userProfile.countryCode] : null;
    const countryStateCodes = (country && country.stateCodes) || [];
    const countryJurisdictions = (country && country.jurisdictions) || [];
    const hasStateCodes = countryStateCodes.length > 0;
    if (!hasStateCodes && countryJurisdictions.length > 1) {
        if (!userProfile.jurisdiction) fieldErrors.jurisdiction = 'Jurisdiction is required';
    }
    if (hasStateCodes) {
        if (!userProfile.state) fieldErrors.state = errorCodes.MSG_STATE_REQUIRED();
        if (!userProfile.zip && userProfile.countryCode === 'US') fieldErrors.zip = errorCodes.MSG_ZIP_REQUIRED();
    }
    if (ssoEnvironment && ssoEnvironment.verifyPhone) {
        const phoneVerified = userProfile.phoneCode + '-' + userProfile.phone === userProfile.phoneStatus;
        const altPhoneVerified = userProfile.altPhoneCode + '-' + userProfile.altPhone === userProfile.altPhoneStatus;
        if (!phoneVerified && !altPhoneVerified) {
            fieldErrors.phone = 'Phone or Altphone number needs verification';
        }
    }
    if (userProfile.phone && userProfile.phone.length > 0 && areacodes && areacodes.length > 0) {
        const phoneCodeEntry = areacodes.find(code => code.code === userProfile.phoneCode);
        if (phoneCodeEntry && phoneCodeEntry.phoneNumRegex && !new RegExp(phoneCodeEntry.phoneNumRegex).test(userProfile.phone)) {
            fieldErrors.phone = errorCodes.MSG_PHONE_INVALID();
        }
    }
    if (userProfile.altPhone && userProfile.altPhone.length > 0 && areacodes && areacodes.length > 0) {
        const altPhoneCodeEntry = areacodes.find(code => code.code === userProfile.altPhoneCode);
        if (altPhoneCodeEntry && altPhoneCodeEntry.phoneNumRegex && !new RegExp(altPhoneCodeEntry.phoneNumRegex).test(userProfile.altPhone)) {
            fieldErrors.altPhone = errorCodes.MSG_ALT_PHONE_INVALID();
        }
    }
    return fieldErrors;
}

const UserContact = ({ userProfile, onProfileChange, readOnly, authenticatedUser, launcher, countries, countryByCode, errors = {}, onClearError }) => {
    const [alert, setAlert] = useState(null);
    const [phoneVerifierData, setPhoneVerifierData] = useState(null);

    const sort_by_key = useCallback((array, key) => {
        return array.sort(function (a, b) {
            let x = a[key];
            let y = b[key];
            return ((x < y) ? -1 : ((x > y) ? 1 : 0));
        });
    }, []);

    const getAreacodes = useCallback(() => {
        let areacodes = [];
        areacodes.push({ countryCode: 'US', countryName: 'United States', phoneCode: '+1', displayCode: 'US +1', code: 'US+1' });
        if (countries && countries.length > 0) {
            for (let country of sort_by_key([...countries], 'countryCode')) {
                if (country.countryCode != 'US') {
                    areacodes.push({
                        ...country,
                        displayCode: country.countryCode + ' ' + country.phoneCode,
                        code: country.countryCode + country.phoneCode
                    });
                }
            }
        }
        return areacodes;
    }, [countries, sort_by_key]);

    // countries arrive pre-sorted from the server (US first, then alphabetical by name)
    const getCountries = useCallback(() => countries || [], [countries]);

    const getStateCodes = useCallback(() => {
        return (countryByCode && userProfile.countryCode && countryByCode[userProfile.countryCode]?.stateCodes) || [];
    }, [countryByCode, userProfile.countryCode]);

    // Reset state field when country changes or when stateCodes load and the stored
    // value doesn't match any code in the new list (e.g. existing users with free-text state)
    useEffect(() => {
        if (!userProfile.countryCode || !userProfile.state) return;
        const codes = countryByCode && countryByCode[userProfile.countryCode]?.stateCodes;
        if (!codes || codes.length === 0) return;
        const isValid = codes.some(c => c.code === userProfile.state);
        if (!isValid) {
            onProfileChange({ ...userProfile, state: '' });
        }
    }, [userProfile.countryCode, countryByCode]);

    const isPhoneVerified = () => userProfile.phoneCode + '-' + userProfile.phone === userProfile.phoneStatus;
    const isAltPhoneVerified = () => userProfile.altPhoneCode + '-' + userProfile.altPhone === userProfile.altPhoneStatus;

    const onValueChange = (event, key, maxLength) => {
        setAlert(null);
        if (onClearError) onClearError(key);
        let newValue = event.target.value;
        if (newValue && maxLength && newValue.length > maxLength) {
            setAlert({ message: errorCodes.MSG_MAX_LENGTH(maxLength), severity: 'error' });
            return;
        }
        onProfileChange({ ...userProfile, [key]: event.target.value });
    };

    const onDropdownChange = (val, key) => {
        setAlert(null);
        if (onClearError) onClearError(key);
        onProfileChange({ ...userProfile, [key]: val });
    };

    const onAreaCodeChange = (val, key) => {
        setAlert(null);
        if (onClearError) onClearError(key);
        onProfileChange({ ...userProfile, [key]: val });
    };

    const onJurisdictionChange = (val) => {
        setAlert(null);
        if (onClearError) ['countryCode', 'jurisdiction', 'state', 'zip'].forEach(f => onClearError(f));
        const country = countryByCode[userProfile.countryCode];
        const countryJurisdictions = (country && country.jurisdictions) || [];
        for (let j of countryJurisdictions) {
            if (j.name == val) {
                const jurCountry = countryByCode[j.code];
                const hasStateCodes = jurCountry && jurCountry.stateCodes && jurCountry.stateCodes.length > 0;
                onProfileChange({ ...userProfile, jurisdiction: j.name, country: j.country, countryCode: j.code, state: hasStateCodes ? '' : j.name });
            }
        }
    };

    const onCountryChange = (val) => {
        setAlert(null);
        if (onClearError) ['countryCode', 'jurisdiction', 'state', 'zip'].forEach(f => onClearError(f));
        const country = countryByCode[val];
        if (country !== null && country !== undefined) {
            let newVal = { country: country.countryName, countryCode: country.countryCode };
            const countryJurisdictions = country.jurisdictions || [];
            const hasStateCodes = country.stateCodes && country.stateCodes.length > 0;
            if (countryJurisdictions.length > 1) {
                newVal.jurisdiction = '';
                newVal.state = '';
                newVal.zip = '';
            } else if (countryJurisdictions.length === 1) {
                newVal.jurisdiction = countryJurisdictions[0].name;
                // Pre-fill state with jurisdiction name for single-jurisdiction countries
                // that have no state codes dropdown — saves the user a manual entry
                newVal.state = hasStateCodes ? '' : countryJurisdictions[0].name;
                newVal.zip = '';
            } else {
                newVal.jurisdiction = country.countryName;
                newVal.state = '';
                newVal.zip = '';
            }
            onProfileChange({ ...userProfile, ...newVal });
        }
    };

    const onPhoneConfirmed = (data) => {
        onProfileChange({ ...userProfile, ...data });
        setPhoneVerifierData(null);
    };

    if (!userProfile) return null;

    const selectedCountry = countryByCode && userProfile.countryCode ? countryByCode[userProfile.countryCode] : null;
    const countryStateCodes = (selectedCountry && selectedCountry.stateCodes) || [];
    const countryJurisdictions = (selectedCountry && selectedCountry.jurisdictions) || [];

    let computedShowJurisdiction = false;
    if (userProfile.countryCode) {
        const hasStateCodes = countryStateCodes.length > 0;
        if (!hasStateCodes && countryJurisdictions.length > 1) {
            computedShowJurisdiction = true;
        }
    }

    return (
        <Box>
            <MuiDialog open={!!phoneVerifierData} onClose={() => setPhoneVerifierData(null)} maxWidth={launcher ? 'sm' : 'md'} fullWidth
                aria-labelledby="user-contact-verify-phone-title"
                aria-describedby="user-contact-verify-phone-description">
                <DialogTitleWithClose severity="info" id="user-contact-verify-phone-title" onClose={() => setPhoneVerifierData(null)} dataAutomationId="user_contact_verify_phone_title">{getLabelFromJspVariables('title_verify_phone')}</DialogTitleWithClose>
                <DialogContent id="user-contact-verify-phone-description">
                    {phoneVerifierData &&
                        <PhoneVerifier
                            userProfile={userProfile}
                            authenticatedUser={authenticatedUser}
                            which={phoneVerifierData.which}
                            phoneCode={phoneVerifierData.phoneCode}
                            phoneNumber={phoneVerifierData.phoneNumber}
                            onConfirm={onPhoneConfirmed}
                            onCancel={() => setPhoneVerifierData(null)}
                        />
                    }
                </DialogContent>
            </MuiDialog>
            {alert && <Alert id="UserContactAlert" data-automation-id="user_contact_alert" severity={alert.severity} sx={{ mb: 1 }} onClose={() => setAlert(null)}>{alert.message}</Alert>}

            {launcher ?
                <Grid container spacing={2}>
                    <Grid xs={6}>
                        <TextInput label={getLabelFromJspVariables('label_first_name_short')}
                            id='first_name_input'
                            dataAutomationId='first_name_input'
                            value={userProfile.firstName}
                            required={true}
                            disabled={readOnly}
                            error={!!errors.firstName}
                            helperText={errors.firstName || ''}
                            onChange={e => onValueChange(e, 'firstName', 100)}
                        />
                    </Grid>
                    <Grid xs={6}>
                        <TextInput label={getLabelFromJspVariables('label_last_name_short')}
                            id='last_name_input'
                            dataAutomationId='last_name_input'
                            value={userProfile.lastName}
                            required={true}
                            disabled={readOnly}
                            error={!!errors.lastName}
                            helperText={errors.lastName || ''}
                            onChange={e => onValueChange(e, 'lastName', 100)}
                        />
                    </Grid>
                </Grid>
                :
                <Grid container spacing={2}>
                    <Grid xs={12} lg={6}>
                        <TextInput label="First Name"
                            id='form_input_two_column_first_name'
                            dataAutomationId='form_input_two_column_first_name'
                            required={true}
                            value={userProfile.firstName}
                            disabled={readOnly}
                            error={!!errors.firstName}
                            helperText={errors.firstName || ''}
                            onChange={e => onValueChange(e, 'firstName', 100)}
                        />
                    </Grid>
                    <Grid xs={12} lg={6}>
                        <TextInput label="Last Name"
                            id='form_input_two_column_last_name'
                            dataAutomationId='form_input_two_column_last_name'
                            required={true}
                            value={userProfile.lastName}
                            disabled={readOnly}
                            error={!!errors.lastName}
                            helperText={errors.lastName || ''}
                            onChange={e => onValueChange(e, 'lastName', 100)}
                        />
                    </Grid>
                </Grid>
            }
            {userProfile.email &&
                <Grid container spacing={2}>
                    <Grid xs={12} md={6}>
                        <TextInput label="Email"
                            id='work_email_input'
                            dataAutomationId='work_email_input'
                            value={userProfile.email}
                            disabled={true}
                        />
                    </Grid>
                </Grid>
            }
            <Grid container spacing={2}>
                <Grid xs={12} md={6}>
                    <PhoneInput
                        label={getLabelFromJspVariables('label_phone')}
                        value={userProfile.phone}
                        areaCode={userProfile.phoneCode}
                        areacodes={getAreacodes()}
                        onChange={e => onValueChange(e, 'phone', 30)}
                        required={true}
                        readOnly={readOnly}
                        onAreaCodeChange={e => onAreaCodeChange(e, 'phoneCode')}
                        verified={isPhoneVerified()}
                        verify={() => setPhoneVerifierData({ which: 'pri', phoneCode: userProfile.phoneCode, phoneNumber: userProfile.phone })}
                        phoneError={!!errors.phone}
                        phoneHelperText={errors.phone || ''}
                        codeError={!!errors.phoneCode}
                        codeHelperText={errors.phoneCode || ''}
                    />
                </Grid>
                <Grid xs={12} md={6}>
                    <PhoneInput
                        label={getLabelFromJspVariables('label_alternate_phone')}
                        value={userProfile.altPhone}
                        areacodes={getAreacodes()}
                        onChange={e => onValueChange(e, 'altPhone', 30)}
                        areaCode={userProfile.altPhoneCode}
                        onAreaCodeChange={e => onAreaCodeChange(e, 'altPhoneCode')}
                        readOnly={readOnly}
                        verified={isAltPhoneVerified()}
                        verify={() => setPhoneVerifierData({ which: 'alt', phoneCode: userProfile.altPhoneCode, phoneNumber: userProfile.altPhone })}
                        phoneError={!!errors.altPhone}
                        phoneHelperText={errors.altPhone || ''}
                        codeError={!!errors.altPhoneCode}
                        codeHelperText={errors.altPhoneCode || ''}
                    />
                </Grid>
            </Grid>
            <Grid container spacing={2}>
                <Grid xs={12} md={6}>
                    <TextInput
                        label={getLabelFromJspVariables('label_address1')}
                        id="address1_input"
                        dataAutomationId="address1_input" 
                        value={userProfile.address1}
                        required={true}
                        disabled={readOnly}
                        error={!!errors.address1}
                        helperText={errors.address1 || ''}
                        onChange={e => onValueChange(e, 'address1', 100)}
                    />
                </Grid>
                <Grid xs={12} md={6}>
                    <TextInput
                        label={getLabelFromJspVariables('label_address2')}
                        id="address2_input"
                        dataAutomationId="address2_input"                        
                        value={userProfile.address2}
                        disabled={readOnly}
                        onChange={e => onValueChange(e, 'address2', 100)}
                    />
                </Grid>
            </Grid>
            <Grid container spacing={2}>
                <Grid xs={6} md={4} lg={3}>
                    <Dropdown
                        selectedValue={userProfile.countryCode}
                        id='country_dropdown'
                        name='countryCode'
                        dataAutomationId='country_dropdown'
                        label={getLabelFromJspVariables('label_country')}
                        labelProperty="countryName"
                        valueProperty="countryCode"
                        required={true}
                        disabled={readOnly}
                        error={!!errors.countryCode}
                        helperText={errors.countryCode || ''}
                        options={getCountries()}
                        updateValue={e => onCountryChange(e)}
                    />
                </Grid>
                {computedShowJurisdiction && <Grid xs={6} md={4} lg={3}>
                    <Dropdown
                        selectedValue={userProfile.jurisdiction}
                        id='jurisdiction_dropdown'
                        name='jurisdiction'
                        dataAutomationId='jurisdiction_dropdown'
                        label={getLabelFromJspVariables('label_jurisdiction')}
                        labelProperty="name"
                        valueProperty="name"
                        required={true}
                        disabled={readOnly}
                        error={!!errors.jurisdiction}
                        helperText={errors.jurisdiction || ''}
                        options={countryJurisdictions}
                        updateValue={e => onJurisdictionChange(e)}
                    />
                </Grid>}
                {!computedShowJurisdiction && <Grid xs={12} md={4} lg={3}>
                    {countryStateCodes.length > 0 ?
                        <Dropdown
                            selectedValue={userProfile.state}
                            id='state_dropdown'
                            name='state'
                            dataAutomationId='state_dropdown'
                            label={getLabelFromJspVariables('label_state')}
                            labelProperty="name"
                            valueProperty="code"
                            required={true}
                            disabled={readOnly}
                            error={!!errors.state}
                            helperText={errors.state || ''}
                            options={getStateCodes()}
                            updateValue={val => onDropdownChange(val, 'state')}
                        />
                        : <TextInput
                            label={getLabelFromJspVariables('label_state')}
                            id='state_input'
                            dataAutomationId='state_input'
                            value={userProfile.state || ''}
                            disabled={readOnly}
                            error={!!errors.state}
                            helperText={errors.state || ''}
                            onChange={e => onValueChange(e, 'state', 100)}
                        />}
                </Grid>}
                <Grid xs={6} md={4} lg={3}>
                    <TextInput label={getLabelFromJspVariables('label_city')}
                        id='city_input'
                        dataAutomationId='city_input'
                        value={userProfile.city}
                        required={true}
                        disabled={readOnly}
                        error={!!errors.city}
                        helperText={errors.city || ''}
                        onChange={e => onValueChange(e, 'city', 100)}
                    />
                </Grid>
                <Grid xs={12} md={4} lg={3}>
                    <TextInput
                        label={getLabelFromJspVariables('label_zip')}
                        required={userProfile.countryCode === 'US'}
                        id='zip_input'
                        dataAutomationId='zip_input'
                        value={userProfile.zip || ''}
                        disabled={readOnly}
                        error={!!errors.zip}
                        helperText={errors.zip || ''}
                        onChange={e => onValueChange(e, 'zip', 20)}
                    />
                </Grid>
            </Grid>
        </Box>
    );
};

export default UserContact;
